import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  ArrowUpRight, ArrowDownLeft, Loader2, CheckCircle2, X, FileText,
  Search, AlertCircle, Ban, FolderOpen, Eye, ExternalLink, ChevronRight,
  RefreshCw, Unlink, ZoomIn, ZoomOut, Download
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Pending() {
  const { token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTx, setSelectedTx] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // PDF Preview
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(100);
  
  // Modals
  const [showDiversModal, setShowDiversModal] = useState(false);
  const [showIgnoreModal, setShowIgnoreModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [diversLibelle, setDiversLibelle] = useState("");
  const [diversCategorie, setDiversCategorie] = useState("Autre");
  const [ignoreRaison, setIgnoreRaison] = useState("");
  const [manualSearch, setManualSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  
  // Processing state
  const [processing, setProcessing] = useState(false);
  const [runningMatching, setRunningMatching] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/bridge-v2/rapprochement/pending`, { headers });
      const txList = res.data.transactions || [];
      setTransactions(txList);
      
      // Sélectionner la première transaction automatiquement
      if (txList.length > 0 && !selectedTx) {
        setSelectedTx(txList[0]);
      }
    } catch (err) {
      console.error("Erreur chargement pending:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPending();
  }, [token]);

  // Charger les suggestions quand une transaction est sélectionnée
  useEffect(() => {
    if (selectedTx) {
      // Si les suggestions sont déjà dans la transaction
      if (selectedTx.suggestions && selectedTx.suggestions.length > 0) {
        setSuggestions(selectedTx.suggestions);
      } else {
        loadSuggestions(selectedTx);
      }
      setPdfUrl(null);
      setShowPdfPreview(false);
    }
  }, [selectedTx?.id]);

  const loadSuggestions = async (tx) => {
    if (!tx || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setLoadingSuggestions(true);
      const sens = tx.amount > 0 ? 'vente' : 'achat';
      const res = await axios.get(`${API}/api/bridge-v2/rapprochement/search-invoices`, {
        params: {
          transaction_id: tx.id,
          montant: Math.abs(tx.amount),
          date: tx.date,
          sens: sens
        },
        headers
      });
      setSuggestions(res.data.factures || []);
    } catch (err) {
      console.error("Erreur chargement suggestions:", err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  };

  // Lancer le matching auto
  const handleRunMatching = async () => {
    if (!token || runningMatching) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setRunningMatching(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/run-matching`, {}, { headers });
      await fetchPending();
    } catch (err) {
      console.error("Erreur matching:", err);
    } finally {
      setRunningMatching(false);
    }
  };

  // Prévisualiser un PDF
  const handlePreviewPdf = async (facture) => {
    if (!token) return;
    
    const isVente = selectedTx?.amount > 0;
    const endpoint = isVente 
      ? `${API}/api/pdfs/${facture.facture_numero || facture.numero}?token=${token}`
      : `${API}/api/supplier-invoices/${facture.facture_numero || facture.numero}/pdf?token=${token}`;
    
    setPdfUrl(endpoint);
    setShowPdfPreview(true);
  };

  // Valider un match
  const handleValidateMatch = async (facture) => {
    if (!selectedTx || processing || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setProcessing(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/validate`, {
        transaction_id: selectedTx.id,
        facture_numero: facture.facture_numero || facture.numero,
        facture_type: selectedTx.amount > 0 ? 'vente' : 'achat'
      }, { headers });
      
      // Rafraîchir et passer à la suivante
      const remaining = transactions.filter(t => t.id !== selectedTx.id);
      if (remaining.length > 0) {
        setSelectedTx(remaining[0]);
      } else {
        setSelectedTx(null);
      }
      setSuggestions([]);
      setPdfUrl(null);
      await fetchPending();
    } catch (err) {
      console.error("Erreur validation:", err);
      alert("Erreur lors de la validation");
    } finally {
      setProcessing(false);
    }
  };

  // Classer en divers
  const handleDivers = async () => {
    if (!selectedTx || !diversLibelle || processing || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setProcessing(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/divers`, {
        transaction_id: selectedTx.id,
        libelle: diversLibelle,
        categorie: diversCategorie
      }, { headers });
      
      setShowDiversModal(false);
      setDiversLibelle("");
      setDiversCategorie("Autre");
      
      const remaining = transactions.filter(t => t.id !== selectedTx.id);
      setSelectedTx(remaining.length > 0 ? remaining[0] : null);
      setSuggestions([]);
      await fetchPending();
    } catch (err) {
      console.error("Erreur divers:", err);
      alert("Erreur lors du classement");
    } finally {
      setProcessing(false);
    }
  };

  // Ignorer
  const handleIgnore = async () => {
    if (!selectedTx || !ignoreRaison || processing || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      setProcessing(true);
      await axios.post(`${API}/api/bridge-v2/rapprochement/ignore`, {
        transaction_id: selectedTx.id,
        raison: ignoreRaison
      }, { headers });
      
      setShowIgnoreModal(false);
      setIgnoreRaison("");
      
      const remaining = transactions.filter(t => t.id !== selectedTx.id);
      setSelectedTx(remaining.length > 0 ? remaining[0] : null);
      setSuggestions([]);
      await fetchPending();
    } catch (err) {
      console.error("Erreur ignore:", err);
      alert("Erreur lors de l'ignorance");
    } finally {
      setProcessing(false);
    }
  };

  // Recherche manuelle
  const handleManualSearch = async () => {
    if (!manualSearch || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const sens = selectedTx?.amount > 0 ? 'vente' : 'achat';
      const res = await axios.get(`${API}/api/bridge-v2/invoices/candidates`, {
        params: {
          amount: selectedTx?.amount || 0,
          search: manualSearch
        },
        headers
      });
      setSearchResults(res.data.invoices || []);
    } catch (err) {
      console.error("Erreur recherche:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-16" data-testid="no-pending">
        <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Tout est à jour !</h2>
        <p className="text-slate-500 mb-6">Aucune transaction en attente de validation</p>
        <Button onClick={handleRunMatching} disabled={runningMatching}>
          {runningMatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Relancer le matching
        </Button>
      </div>
    );
  }

  const isCredit = selectedTx?.amount > 0;

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="pending-page">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">À valider</h1>
          <p className="text-slate-500">{transactions.length} transactions en attente</p>
        </div>
        <Button onClick={handleRunMatching} disabled={runningMatching} variant="outline">
          {runningMatching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Relancer le matching
        </Button>
      </div>

      {/* Split View */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Colonne gauche - Liste des transactions */}
        <div className="col-span-3 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-2 px-3 py-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Transactions ({transactions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-2">
              <div className="space-y-1">
                {transactions.map((tx) => {
                  const txIsCredit = tx.amount > 0;
                  const isSelected = selectedTx?.id === tx.id;
                  const hasSuggestions = (tx.suggestions_count || tx.suggestions?.length || 0) > 0;
                  
                  return (
                    <div
                      key={tx.id}
                      onClick={() => setSelectedTx(tx)}
                      className={`p-2 rounded-lg cursor-pointer transition-all border ${
                        isSelected 
                          ? 'bg-blue-50 border-blue-300 shadow-sm' 
                          : 'hover:bg-slate-50 border-transparent'
                      }`}
                      data-testid={`pending-tx-${tx.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <div className={`p-0.5 rounded ${txIsCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                              {txIsCredit ? (
                                <ArrowDownLeft className="w-3 h-3 text-green-600" />
                              ) : (
                                <ArrowUpRight className="w-3 h-3 text-red-600" />
                              )}
                            </div>
                            <span className="text-xs text-slate-500">{formatDate(tx.date)}</span>
                          </div>
                          <p className="font-medium text-xs text-slate-900 truncate mt-0.5">
                            {tx.clean_description || '-'}
                          </p>
                        </div>
                        <span className={`font-bold text-xs whitespace-nowrap ${txIsCredit ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(tx.amount)}
                        </span>
                      </div>
                      {hasSuggestions && (
                        <Badge variant="outline" className="text-[10px] mt-1 bg-green-50 text-green-700 border-green-200">
                          {tx.suggestions_count || tx.suggestions?.length} match
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Colonne centrale - Détails et suggestions */}
        <div className="col-span-5 flex flex-col min-h-0 gap-3">
          {selectedTx ? (
            <>
              {/* Détails de la transaction sélectionnée */}
              <Card>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`p-1.5 rounded-lg ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                          {isCredit ? (
                            <ArrowDownLeft className="w-4 h-4 text-green-600" />
                          ) : (
                            <ArrowUpRight className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-sm text-slate-900">
                            {selectedTx.clean_description || '-'}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(selectedTx.date)}</p>
                        </div>
                      </div>
                      {selectedTx.provider_description && selectedTx.provider_description !== selectedTx.clean_description && (
                        <p className="text-xs text-slate-400 mt-1 pl-8 truncate">
                          {selectedTx.provider_description}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(selectedTx.amount)}
                      </p>
                      <Badge className={`text-[10px] ${isCredit ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {isCredit ? 'Crédit • Vente' : 'Débit • Achat'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Actions rapides */}
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                    <Button variant="outline" size="sm" onClick={() => setShowSearchModal(true)}>
                      <Search className="w-3 h-3 mr-1" />
                      Rechercher
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowDiversModal(true)}>
                      <FolderOpen className="w-3 h-3 mr-1" />
                      Divers
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowIgnoreModal(true)} className="text-red-600 hover:text-red-700">
                      <Ban className="w-3 h-3 mr-1" />
                      Ignorer
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Suggestions de factures */}
              <Card className="flex-1 flex flex-col min-h-0">
                <CardHeader className="pb-2 px-4 py-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4" />
                    Factures correspondantes
                    {suggestions.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{suggestions.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto px-3 py-2">
                  {loadingSuggestions ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      <AlertCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p className="font-medium text-sm">Aucune facture correspondante</p>
                      <p className="text-xs mt-1">
                        Montant: {formatCurrency(Math.abs(selectedTx.amount))}
                      </p>
                      <Button variant="link" size="sm" onClick={() => setShowSearchModal(true)} className="mt-2">
                        Rechercher manuellement
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {suggestions.map((fac, idx) => (
                        <div
                          key={fac.facture_numero || fac.numero || idx}
                          className={`p-3 border rounded-lg transition-all ${
                            fac.auto_validation 
                              ? 'border-green-200 bg-green-50/50' 
                              : 'border-slate-200 hover:border-blue-200 hover:bg-blue-50/30'
                          }`}
                          data-testid={`suggestion-${fac.facture_numero || fac.numero}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-sm text-blue-600">{fac.facture_numero || fac.numero}</span>
                                {fac.auto_validation && (
                                  <Badge className="bg-green-100 text-green-700 text-[10px]">
                                    Match auto
                                  </Badge>
                                )}
                                {fac.score >= 70 && !fac.auto_validation && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">
                                    Score {fac.score}
                                  </Badge>
                                )}
                              </div>
                              <p className="font-medium text-sm text-slate-900 mt-0.5">
                                {fac.client || fac.fournisseur || '-'}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatDate(fac.date)}
                              </p>
                              {fac.raisons && fac.raisons.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {fac.raisons.slice(0, 2).map((r, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px] bg-slate-50">
                                      {r}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="text-right flex flex-col items-end gap-1">
                              <p className="font-bold text-sm">{formatCurrency(fac.total_ttc)}</p>
                              <div className="flex gap-1">
                                {fac.has_pdf && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                    onClick={() => handlePreviewPdf(fac)}
                                  >
                                    <Eye className="w-3 h-3" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  className="h-7"
                                  onClick={() => handleValidateMatch(fac)}
                                  disabled={processing}
                                >
                                  {processing ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Valider
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="flex-1 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <ChevronRight className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Sélectionnez une transaction</p>
              </div>
            </Card>
          )}
        </div>

        {/* Colonne droite - Preview PDF */}
        <div className="col-span-4 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-2 px-4 py-2 flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-500">
                Prévisualisation PDF
              </CardTitle>
              {showPdfPreview && pdfUrl && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPdfZoom(z => Math.max(50, z - 25))}>
                    <ZoomOut className="w-3 h-3" />
                  </Button>
                  <span className="text-xs text-slate-500">{pdfZoom}%</span>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPdfZoom(z => Math.min(200, z + 25))}>
                    <ZoomIn className="w-3 h-3" />
                  </Button>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="sm" className="h-7 px-2">
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </a>
                </div>
              )}
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-2">
              {showPdfPreview && pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0 rounded"
                  style={{ transform: `scale(${pdfZoom / 100})`, transformOrigin: 'top left', width: `${10000 / pdfZoom}%`, height: `${10000 / pdfZoom}%` }}
                  title="PDF Preview"
                />
              ) : (
                <div className="h-full flex items-center justify-center bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <div className="text-center text-slate-400">
                    <Eye className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">Cliquez sur l'icône <Eye className="w-4 h-4 inline" /> d'une facture</p>
                    <p className="text-xs">pour prévisualiser le PDF</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal Divers */}
      <Dialog open={showDiversModal} onOpenChange={setShowDiversModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Classer en Divers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Libellé</label>
              <Input
                value={diversLibelle}
                onChange={(e) => setDiversLibelle(e.target.value)}
                placeholder="Ex: Frais bancaires, Abonnement..."
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Catégorie</label>
              <Select value={diversCategorie} onValueChange={setDiversCategorie}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Frais bancaires">Frais bancaires</SelectItem>
                  <SelectItem value="Abonnements">Abonnements</SelectItem>
                  <SelectItem value="Fournitures">Fournitures</SelectItem>
                  <SelectItem value="Charges">Charges</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiversModal(false)}>Annuler</Button>
            <Button onClick={handleDivers} disabled={!diversLibelle || processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ignorer */}
      <Dialog open={showIgnoreModal} onOpenChange={setShowIgnoreModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ignorer cette transaction</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Raison</label>
            <Textarea
              value={ignoreRaison}
              onChange={(e) => setIgnoreRaison(e.target.value)}
              placeholder="Expliquez pourquoi cette transaction est ignorée..."
              className="mt-1"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIgnoreModal(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleIgnore} disabled={!ignoreRaison || processing}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ignorer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Recherche manuelle */}
      <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Rechercher une facture</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={manualSearch}
                onChange={(e) => setManualSearch(e.target.value)}
                placeholder="Rechercher par numéro, client, fournisseur..."
                onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
              />
              <Button onClick={handleManualSearch}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {searchResults.map((fac, idx) => (
                  <div key={fac.numero || idx} className="p-3 border rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{fac.numero}</p>
                      <p className="text-sm text-slate-600">{fac.client || fac.fournisseur}</p>
                      <p className="text-xs text-slate-500">{formatDate(fac.date)} • {formatCurrency(fac.total_ttc)}</p>
                    </div>
                    <Button size="sm" onClick={() => {
                      handleValidateMatch(fac);
                      setShowSearchModal(false);
                    }}>
                      Sélectionner
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
